'use client';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
} from 'react';
import { useSessionStorage } from '@/hooks/useStorage';
import { useToggle } from '@/hooks/useToggle';
import { clampQuantity } from '@/utils/pricing';

type CheckoutContextValue = {
  acceptsTerms: boolean;
  isProfessional: boolean;
  email: string;
  quantity: number;
  setEmail: (email: string) => void;
  setQuantity: (quantity: number) => void;
  toggleProfessional: () => void;
  toggleTerms: () => void;
};

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

export default function CheckoutProvider({ children }: PropsWithChildren) {
  const [quantity, setQuantity] = useState(1); // Default to 1 item
  const [email, setEmail] = useSessionStorage('checkoutEmail', '', true);
  const [isProfessional, toggleProfessional] = useToggle(false);
  const [acceptsTerms, toggleTerms] = useToggle(false);

  const handleChangeQuantity = (val: number) => setQuantity(clampQuantity(val));

  const contextValue = {
    acceptsTerms,
    isProfessional,
    email: email || '',
    quantity,
    setEmail,
    setQuantity: handleChangeQuantity,
    toggleProfessional,
    toggleTerms,
  };

  return (
    <CheckoutContext.Provider value={contextValue}>
      {children}
    </CheckoutContext.Provider>
  );
}

export function useCheckout() {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw Error('useCheckout must be used within the CheckoutProvider');
  }
  return context;
}
